import React, { useState } from "react";
import { toastr } from "react-redux-toastr";
import { Controller, useForm } from "react-hook-form";
import { DropdownList } from "react-widgets";
import {
  useLead,
  IMarkAsLost,
  ILeadAction,
  Lead,
  formatDate,
  DateFormats,
  createStartOfDayWithTimeZone,
  getMinuteDifference,
  State,
  LeadQuality,
  LeadQualityChange,
  getMinutes,
} from "../../../../core";
import api from "../../../../api";
import { useMasterData } from "../../../../core/hooks/useMasterData";
import ErrorMessage from "../../../../components/text/ErrorMessage";
import moment from "moment";
import BlockInput from "../../../../components/block-input";
import RemoteDataDropDown from "../../../../components/dropdown/RemoteDataDropDown";
import {
  ITimePeriodData,
  isTimePeriodValid,
} from "../../../../core/components/time-period/custom-time-period";
import {
  IBigCalendarEvent,
  IBigCalendarResource,
} from "../../../../core/components/calendar/big-calendar-component";
import { IFormFilterData } from "../activities/edit-event-popup";
import {
  combineDateAndTimeMoment,
  combineDateAndTime,
} from "../../../../utils/CombineDateAndTime";
import { ActivityModel } from "../activities/add-activity";
import { getActivityDisplayDetails } from "../../../../core/components/calendar/calendar-mapper-utils";
import { isObject } from "util";
import { SingleDayTimePeriod } from "../../../../core/components/time-period/single-day-time-period";
import { AutoCompleteInput } from "../../../../components/form-group";
import OwnerBlock from "../assign-users/OwnerBlock";
import { EditEventCalendarWraper } from "../../../../core/components/calendar/edit-event-calendar-wraper";
import { useDataLoader } from "react-remote-data-hooks";
import { useSelector } from "react-redux";
import { useAppSettings } from "../../../../core/hooks/useAppSettings";
import { useLeadActivities } from "../../../../core/hooks/useLeadActivities";

export interface FormProps {
  reason: string;
  nextContactDate?: Date;
  comment?: string;
  contactAfter?: string;
  showroom?: string;
  salesPerson?: string;
  scheduleStartDate?: Date;
  scheduleEndDate?: Date;
  note?: string;
  startTime?: string;
  endTime?: string;
  busyEvent?: boolean;
  timePeriod?: ITimePeriodData;
  otherReason: string;
}

const contactOptions = [
  { label: "Next Week", value: "nextWeek", number: 7 },
  { label: "After 2 Week", value: "after2Week", number: 14 },
  {
    label: "After 1 Month",
    value: "after1Month",
    number: 30,
  },
  {
    label: "After 2 Month",
    value: "after2Month",
    number: 60,
  },
];

const MarkAsLost: React.FunctionComponent<{
  leadId: string;
  lead?: Lead;
}> = (props) => {
  const appSettings = useAppSettings(api.settings);
  const leadState = useSelector((state: State) => state.lead);
  const [lead, setLead] = useState(props.lead);
  const masterData = useMasterData(api.settings);
  const { getLeadAction } = useLead(api.lead);
  const [isModelOpen, setIsModelOpen] = useState<boolean>(false);
  const { updateLeadQuality, updating } = useLead(api.lead);
  const { getAllLeadActivities: getLeadActivities } = useLeadActivities(
    api.lead
  );
  const [
    isContactAfterRequired,
    setIsContactAfterRequired,
  ] = React.useState<boolean>(false);
  const {
    control,
    handleSubmit,
    errors,
    reset,
    watch,
    setValue,
  } = useForm<FormProps>({
    reValidateMode: "onChange",
    mode: "onSubmit",
  });

  const action = leadState.leadActions?.find(
    (x) => x.code === "requestACallBack"
  );

  const minimumDurationInMinutes = getMinutes(
    appSettings?.data?.activity?.activityMinimumDuration || {
      value: 15,
      unit: "Minutes",
    }
  );

  const startDateSettingsTime = combineDateAndTime(
    moment().startOf("day").toDate(),
    appSettings?.data?.application?.appointmentTime?.start || "9:30 AM"
  );

  const quality = masterData?.data?.leadQualities?.find(
    (x) => x.code === LeadQuality.NotInterestedNow
  );

  const [currentActivity, setCurrentActivity] = useState<ActivityModel>({
    type: "call",
    title: "",
    timePeriod: {
      startDate: new Date(),
      startTime:
        appSettings?.data?.application?.appointmentTime?.start || "9:00 AM",
      endTime:
        moment(startDateSettingsTime)
          .add(minimumDurationInMinutes, "minute")
          .format(DateFormats.TimeOnlyFormat) || "9:30 AM",
    },
    showRoom: lead?.showroom
      ? {
          _id: lead?.showroom?._id || "",
          title: lead?.showroom?.title,
        }
      : undefined,
    salesPerson: lead?.currentCRMAgent
      ? {
          _id: lead?.currentCRMAgent?._id || "",
          name: lead?.currentCRMAgent?.name,
          knownAs: lead?.currentCRMAgent?.knownAs,
        }
      : lead?.currentSalesPerson
      ? {
          _id: lead?.currentSalesPerson?._id || "",
          name: lead?.currentSalesPerson?.name,
          knownAs: lead?.currentSalesPerson?.knownAs,
        }
      : undefined,
    busyEvent: true,
  });
  const [tempEvent, setTempEvent] = useState<IBigCalendarEvent>();
  const [filter, setFilter] = useState<IFormFilterData>({
    salesPerson: currentActivity?.salesPerson?._id,
    vehicle: currentActivity?.vehicle?._id,
    showRoom: currentActivity?.showRoom?._id,
  });

  const [selectedDate, setSelectedDate] = useState(
    currentActivity?.timePeriod?.startDate ?? new Date()
  );

  const loadShowroom = async () => {
    const response = await api.settings.getShowrooms({ ecommerceOnly: true });
    return response;
  };

  React.useEffect(() => {
    if (tempEvent && filter.salesPerson) {
      setTempEvent({ ...tempEvent, resourceId: filter.salesPerson });
    }
  }, [filter.salesPerson]);

  React.useEffect(() => {
    mapToBigCalendarTempEvent();
  }, []);

  React.useEffect(() => {
    mapToBigCalendarTempEvent();
  }, [currentActivity]);

  const { data: actionData, reload } = useDataLoader<ILeadAction>(() =>
    getLeadAction(props.leadId)
  );

  const mapToBigCalendarTempEvent = () => {
    const activity = currentActivity;

    if (
      activity?.timePeriod?.startDate != null &&
      activity?.timePeriod.startTime !== "" &&
      activity?.timePeriod.endTime !== ""
    ) {
      const startDate = combineDateAndTimeMoment(
        moment(activity?.timePeriod?.startDate).format("YYYY-MM-DD"),
        activity.timePeriod.startTime!
      );
      const endDate = combineDateAndTimeMoment(
        moment(
          activity.timePeriod.endDate ?? activity.timePeriod.startDate
        ).format("YYYY-MM-DD"),
        activity.timePeriod.endTime!
      );

      const event: IBigCalendarEvent = {
        id: activity?.eventId ?? "",
        title: activity?.title ?? "",
        start: startDate,
        end: endDate,
        resourceId: activity.salesPerson?._id ?? filter.salesPerson!,
        appointment: {} as any,
        activityType: "temp",
        displayDetails: {
          icon: "",
          text: "Temp",
          color: "4161C8",
          isDraggable: true,
          isSingleDayEvent: true,
          className: "temp-event",
        },
      };

      setTempEvent(event);
    } else {
      setTempEvent(undefined);
    }
  };

  const isValidTime = (start: string, end: string) => {
    const startOfDay = moment(new Date()).startOf("day").toDate();
    const startDateTime = combineDateAndTime(startOfDay, start);
    const endDateTime = combineDateAndTime(startOfDay, end);
    return moment(startDateTime).isBefore(moment(endDateTime));
  };

  const assignResourceTypeToActivity = (
    clone: ActivityModel,
    resource?: IBigCalendarResource
  ) => {
    if (resource && resource.resourceType === "staff") {
      clone.salesPerson = {
        _id: resource.resourceId,
        name: resource.resourceTitle,
      };
      clone.showRoom = resource.showroom;
    } else {
      clone.vehicleModel = resource?.vehicleModel;
      clone.vehicleVariant = resource?.vehicleVariant;
      clone.showRoom = resource?.showroom;
      clone.vehicle = {
        _id: resource?.resourceId!,
        displayName: resource?.resourceTitle!,
      };
    }
  };

  const assignFiltersFromCalendarResource = (
    resource?: IBigCalendarResource
  ) => {
    if (resource) {
      const filterclone = { ...filter };

      filterclone.salesPerson = resource.resourceId;

      filterclone.showRoom = resource.showroom?._id;
      setFilter(filterclone);
    }
  };

  const onSlotSelection = (
    start: Date,
    end: Date,
    resource?: IBigCalendarResource
  ) => {
    if (resource?.resourceType === "vehicle") {
      return;
    }
    assignFiltersFromCalendarResource(resource);

    if (currentActivity) {
      const clone = { ...currentActivity };
      assignResourceTypeToActivity(clone, resource);

      clone.timePeriod = {
        startDate: start,
        startTime: formatDate(start, DateFormats.TimeOnlyFormat),
        endTime: formatDate(end, DateFormats.TimeOnlyFormat),
        endDate: getActivityDisplayDetails(currentActivity?.type!)
          .isSingleDayEvent
          ? undefined
          : createStartOfDayWithTimeZone(end),
      };
      setCurrentActivity(clone);
    }
  };

  const onSlotClick = (start: Date, resource?: IBigCalendarResource) => {
    if (currentActivity && currentActivity.timePeriod) {
      const endDateTime = combineDateAndTime(
        moment(
          currentActivity?.timePeriod?.endDate ??
            currentActivity?.timePeriod?.startDate
        )
          .startOf("day")
          .toDate(),
        currentActivity?.timePeriod?.endTime!
      );
      const startDateTime = combineDateAndTime(
        moment(currentActivity?.timePeriod?.startDate).startOf("day").toDate(),
        currentActivity?.timePeriod?.startTime!
      );
      const minuteGap = getMinuteDifference(startDateTime, endDateTime);

      const newStartDateTime = start;
      const newEndDateTime = moment(newStartDateTime).add(minuteGap, "minutes");

      const clone = { ...currentActivity };
      if (!clone.timePeriod) {
        clone.timePeriod = {};
      }
      clone.timePeriod.startTime = moment(newStartDateTime).format(
        DateFormats.TimeOnlyFormat
      );

      clone.timePeriod.startDate = createStartOfDayWithTimeZone(start);

      clone.timePeriod.endTime = moment(newEndDateTime).format(
        DateFormats.TimeOnlyFormat
      );
      if (
        getActivityDisplayDetails(currentActivity?.type ?? "").isSingleDayEvent
      ) {
        if (
          !isValidTime(clone.timePeriod.startTime, clone.timePeriod.endTime)
        ) {
          clone.timePeriod.endTime = "11:55 PM";
          clone.timePeriod.endDate = clone.timePeriod.startDate;
        }
      }
      clone.timePeriod = {
        startDate: createStartOfDayWithTimeZone(start),
        startTime: moment(newStartDateTime).format(DateFormats.TimeOnlyFormat),
        endTime: clone.timePeriod.endTime,
        endDate: getActivityDisplayDetails(currentActivity?.type ?? "")
          .isSingleDayEvent
          ? undefined
          : createStartOfDayWithTimeZone(newEndDateTime.toDate()),
      };
      assignResourceTypeToActivity(clone, resource);
      setCurrentActivity(clone);
    }
  };

  const handleDataChange = () => {
    const salesperson: any = watch("salesPerson");
    const showroomData: any = watch("showroom");
    const data: IFormFilterData = {
      salesPerson: isObject(salesperson) ? salesperson._id : salesperson,
      showRoom: isObject(showroomData) ? showroomData._id : showroomData,
    };
    setFilter(data);
  };

  const onTimePeriodChange = (data?: ITimePeriodData) => {
    if (data) {
      if (tempEvent) {
        const clone = { ...tempEvent };
        const startDate = data.startDate || new Date();
        const endDate = data.endDate ?? data.startDate!;

        clone.start = startDate;
        clone.end = endDate;
        if (data.startTime) {
          clone.start = combineDateAndTime(clone.start, data.startTime);
        }
        if (data.endTime) {
          clone.end = combineDateAndTime(clone.end, data.endTime);
        }

        setTempEvent(clone);
        if (currentActivity) {
          const activityClone = { ...currentActivity, timePeriod: data };
          setCurrentActivity(activityClone);
        }
      }
      setSelectedDate(data.startDate || new Date());
    }
  };

  const showroom = isObject(watch("showroom"))
    ? (watch("showroom") as any)._id
    : watch("showroom");

  let minDate: any;
  if (
    appSettings.data?.leadActivityAssignment
      ?.noOfDaysToAllowBackdatedActivity !== undefined &&
    appSettings.data?.leadActivityAssignment
      ?.noOfDaysToAllowBackdatedActivity >= 0
  ) {
    minDate = new Date().setDate(
      new Date().getDate() -
        appSettings.data?.leadActivityAssignment
          ?.noOfDaysToAllowBackdatedActivity
    );
  }

  const renderModel = () => {
    return isModelOpen ? (
      <>
        <div className="modal-open">
          <div className="detail-poupup bg-white position-fixed top-0 w-75 h-100 overflow-auto in">
            <div className="container-fluid bg-gray-100 border-bottom py-3 position-sticky top-0 zIndex-99">
              <div className="row align-items-center">
                <div
                  className="col-auto"
                  onClick={() => {
                    setIsModelOpen(false);
                  }}
                >
                  <i className="icon-close text-gray-500 font-xxl cursor-pointer"></i>
                </div>
                <div className="col">
                  <h5 className="mb-0">Mark as Lost</h5>
                </div>
                <div className="col-auto mt-md-0 mt-1">
                  <OwnerBlock
                    user={lead?.currentCRMAgent || lead?.currentSalesPerson}
                    title={(lead?.currentCRMAgent && "Support Executive") || (lead?.currentSalesPerson && "Transaction Manager")}                
                    className="d-inline-flex align-items-center"
                  ></OwnerBlock>
                </div>
              </div>
            </div>
            <div className="row mx-0 h-100">
              <div className="col-sm-5 col-12 py-4 border-right">
                <div className=" pt-2 pb-4">
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="">
                      <div className="row">
                        <div className="col">
                          <div className="form-group font-weight-semibold mb-3 text-left">
                            <label
                              htmlFor=""
                              className="font-sm text-uppercase"
                            >
                              Reason<span className="text-danger">*</span>
                            </label>
                            <Controller
                              control={control}
                              name="reason"
                              rules={{ required: true }}
                              render={({ onChange, value, name }) => (
                                <DropdownList
                                  value={value}
                                  data={masterData.data?.leadQualityReasons.filter(
                                    (qlty) => qlty.isSelectableForEnquiry
                                  )}
                                  defaultValue={value}
                                  textField={"reason"}
                                  valueField={"code"}
                                  placeholder={"Select Reason"}
                                  onChange={(val) => {
                                    onChange(val.code);
                                    setValue("otherReason", "");
                                  }}
                                />
                              )}
                            />
                            {errors.reason && (
                              <ErrorMessage>Reason is required</ErrorMessage>
                            )}
                          </div>
                        </div>
                      </div>
                      {watch("reason") === "other" && (
                        <div className="row">
                          <div className="col">
                            <div className="form-group font-weight-semibold mb-3 text-left">
                              <Controller
                                control={control}
                                name="otherReason"
                                rules={{ required: true }}
                                render={({ onChange, value, name }) => (
                                  <input
                                    type="text"
                                    className="form-control rounded-sm font-weight-semibold"
                                    placeholder="Enter Reason"
                                    name={name}
                                    value={value}
                                    onChange={onChange}
                                    id="otherReason"
                                  />
                                )}
                              />
                              {errors.otherReason && (
                                <ErrorMessage>Reason is required</ErrorMessage>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="row">
                        <div className="col">
                          <div className="form-group font-weight-semibold mb-3 text-left">
                            <label
                              htmlFor=""
                              className="font-sm text-uppercase"
                            >
                              Comments
                            </label>
                            <Controller
                              control={control}
                              name="comment"
                              render={({ onChange, value, name }) => (
                                <input
                                  type="text"
                                  className="form-control rounded-sm font-weight-semibold"
                                  placeholder="Enter comment"
                                  name={name}
                                  value={value}
                                  onChange={onChange}
                                  id="comment"
                                />
                              )}
                            />
                          </div>
                        </div>
                      </div>
                      {quality?.isContactAfterRequired && (
                        <>
                          <label htmlFor="" className="font-sm text-uppercase">
                            Add Call Back{" "}
                          </label>
                          {/* <div className="form-group font-weight-semibold mb-3">
                            <label
                              htmlFor=""
                              className="font-sm text-uppercase"
                            >
                              Office{" "}
                            </label>
                            <Controller
                              control={control}
                              // rules={{ required: true }}
                              defaultValue={currentActivity?.showRoom}
                              name="showroom"
                              render={({ onChange, value, name }) => (
                                <RemoteDataDropDown
                                  loadData={loadShowroom}
                                  textField="title"
                                  value={value}
                                  defaultValue={value}
                                  valueField="_id"
                                  onChange={(val) => {
                                    onChange(val._id);
                                    setValue("salesPerson", undefined);
                                    handleDataChange();
                                  }}
                                  placeholder="Select Office"
                                ></RemoteDataDropDown>
                              )}
                            />
                            {errors.showroom && (
                              <div>
                                <ErrorMessage>
                                  Office is required
                                </ErrorMessage>
                              </div>
                            )}
                          </div> */}
                          <div className="form-group font-weight-semibold mb-3">
                            <label
                              htmlFor=""
                              className="font-sm text-uppercase"
                            >
                              Date and Time<span className="text-danger">*</span>
                            </label>
                            <div
                              key={JSON.stringify(
                                currentActivity?.timePeriod ?? {}
                              )}
                            >
                              <Controller
                                control={control}
                                rules={{ required: true }}
                                name="timePeriod"
                                defaultValue={currentActivity?.timePeriod}
                                render={({ onChange, value, name }) => (
                                  <SingleDayTimePeriod
                                    onChange={(val: ITimePeriodData) => {
                                      if (isTimePeriodValid(val)) {
                                        onChange(val);
                                        onTimePeriodChange(val);
                                      } else {
                                        onChange(undefined);
                                        onTimePeriodChange(undefined);
                                      }
                                    }}
                                    defaultValue={currentActivity?.timePeriod}
                                    minDate={minDate}
                                    appointmentTime={
                                      appSettings?.data?.application
                                        ?.appointmentTime
                                    }
                                  ></SingleDayTimePeriod>
                                )}
                              />
                            </div>
                            {errors.timePeriod && (
                              <ErrorMessage>
                                Please enter a valid time period
                              </ErrorMessage>
                            )}
                          </div>
                          <div
                            key={
                              currentActivity?.salesPerson?._id ??
                              "" + "request-callback-salespereson"
                            }
                            className="form-group font-weight-semibold mb-3"
                          >
                            <label
                              htmlFor=""
                              className="font-sm text-uppercase"
                            >
                              Transaction Manager<span className="text-danger">*</span>
                            </label>
                            <Controller
                              control={control}
                              rules={{ required: true }}
                              name="salesPerson"
                              defaultValue={currentActivity?.salesPerson}
                              render={({ onChange, value, name }) => (
                                <AutoCompleteInput
                                  defaultValue={value}
                                  placeholder={"Transaction Manager"}
                                  onChange={(val) => {
                                    onChange(val._id);
                                    handleDataChange();
                                  }}
                                  onReset={() => {
                                    onChange(undefined);
                                    handleDataChange();
                                  }}
                                  apiUrl="api/user/sales-persons"
                                  dataLoader={(query?: string) => {
                                    return api.customer.searchSalesPerson(
                                      {
                                        query,
                                        showroomId: showroom,
                                        hideBlocked: true,
                                      },
                                      "request-call-back"
                                    );
                                  }}
                                  valueField={"name"}
                                />
                              )}
                            />
                            {errors.salesPerson && (
                              <ErrorMessage>Transaction Manager is required</ErrorMessage>
                            )}
                          </div>
                          <div className="form-group font-weight-semibold mb-3">
                            <label
                              htmlFor=""
                              className="font-sm text-uppercase"
                            >
                              TYPE
                            </label>
                            <Controller
                              control={control}
                              name="busyEvent"
                              defaultValue={currentActivity?.busyEvent || true}
                              render={({ onChange, value, name }) => (
                                <DropdownList
                                  value={value}
                                  data={[
                                    { label: "Busy", value: true },
                                    // { label: 'Free', value: false },
                                  ]}
                                  textField={"label"}
                                  valueField={"value"}
                                  placeholder={"Select Type"}
                                  onChange={(val) => {
                                    onChange(val.value);
                                  }}
                                  disabled
                                />
                              )}
                            />
                          </div>
                          <div className="form-group font-weight-semibold mb-3">
                            <label
                              htmlFor=""
                              className="font-sm text-uppercase"
                            >
                              Note
                            </label>
                            <Controller
                              control={control}
                              defaultValue={currentActivity?.note}
                              name="note"
                              render={({ onChange, value, name }) => (
                                <textarea
                                  className="form-control rounded-sm font-weight-semibold h-auto"
                                  placeholder="Take a note"
                                  name={name}
                                  value={value}
                                  onChange={onChange}
                                  id="note"
                                />
                              )}
                            />
                            {errors.note && (
                              <ErrorMessage>Note is required</ErrorMessage>
                            )}
                          </div>
                        </>
                      )}

                      <div className="row">
                        <div className="col-auto mr-auto">
                          <button
                            type="button"
                            className="btn btn-outline-dark py-2 font-weight-semibold font-normal px-4 mr-1"
                            onClick={() => setIsModelOpen(false)}
                          >
                            Cancel
                          </button>
                        </div>
                        <div className="col-auto">
                          <button
                            type="submit"
                            className="btn btn-primary py-2 font-weight-semibold font-normal px-4 "
                            // onClick={markLeadAsLost}
                            disabled={updating}
                          >
                            Mark as Lost
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
              <div className="col-sm-7 col-12">
                {quality?.isContactAfterRequired && (
                  <EditEventCalendarWraper
                    eventType={currentActivity?.type}
                    isVehicleOnlyView={false}
                    tempEvent={tempEvent}
                    formFilter={filter}
                    selectedDate={selectedDate!}
                    onDateChange={(date: Date) => {
                      setSelectedDate(date);
                    }}
                    onSlotClick={onSlotClick}
                    onSelectSlot={onSlotSelection}
                  ></EditEventCalendarWraper>
                )}
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </div>
      </>
    ) : (
      <></>
    );
  };
  const onSubmit = async (data: FormProps) => {
    const updateData: LeadQualityChange = {
      quality: LeadQuality.NotInterestedNow,
      reason: data.reason,
      comment: data.comment,
      otherReason: data.otherReason,
    };

    if (data.contactAfter) {
      updateData.contactAfter = data.contactAfter;
      const contactOption = contactOptions.find(
        (x) => x.value === data.contactAfter
      );
      if (contactOption) {
        updateData.nextContactDate = new Date(
          moment()
            .utcOffset(0)
            .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
            .add(contactOption.number, "days")
            .toISOString()
        );
      }
    }

    if (quality?.isContactAfterRequired) {
      // updateData.actionId = action?._id!;
      updateData.showroom = isObject(data?.showroom)
        ? (data.showroom as any)._id
        : data.showroom;
      updateData.salesPersonId = isObject(data.salesPerson)
        ? (data.salesPerson as any)._id
        : data.salesPerson;
      updateData.startDate = data?.scheduleStartDate;
      updateData.note = data?.note;
      updateData.busyEvent = data?.busyEvent;
      if (data?.timePeriod?.startDate && data?.timePeriod?.startTime) {
        updateData.startDate = combineDateAndTime(
          createStartOfDayWithTimeZone(data?.timePeriod?.startDate),
          data?.timePeriod.startTime
        );
        updateData.nextContactDate = combineDateAndTime(
          createStartOfDayWithTimeZone(data?.timePeriod?.startDate),
          data?.timePeriod.startTime
        );
      }

      if (data?.timePeriod?.startDate && data?.timePeriod?.endTime) {
        updateData.endDate = combineDateAndTime(
          createStartOfDayWithTimeZone(data?.timePeriod?.startDate),
          data?.timePeriod?.endTime
        );
      }
    }

    updateLeadQuality(
      updateData,
      props.leadId,
      () => {
        toastr.success("Success", "Lead mark as lost successfully");
        setIsModelOpen(false);
        if (props.lead?._id) {
          getLeadActivities(props.lead?._id);
        }
      },
      (err) => {
        toastr.error(
          "Error",
          (err as any).response?.data?.data?.message ||
            "Unable to updated quality"
        );
      }
    );
  };

  return (
    <>
      <button
        onClick={() => setIsModelOpen(true)}
        className="btn btn-outline-primary px-lg-4 px-2 mb-sm-0 mb-1 font-weight-semibold"
      >
        Mark as Lost
      </button>
      {renderModel()}
    </>
  );
};

export default MarkAsLost;
